import { useEffect, useRef, useState } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";
import { parseCsv, buildReport, CSVError } from "@agencylens/connector-csv";
import { renderCSVReport } from "@agencylens/pdf";
import { DEFAULT_AGENCY_BRAND, type CSVUploadMeta, type CSVColumnSpec } from "@agencylens/shared";
import { Button } from "@/components/ui/Button.js";
import { idbGet, idbPut, idbDelete } from "@/lib/idb.js";
import { svgElementToPng } from "@/lib/chart-rasterize.js";
import { useApp } from "@/store/app.js";

const STORAGE_KEY = "uploads:csv:current";

interface StoredCsv {
  meta: CSVUploadMeta;
  rows: Array<Record<string, string | number>>;
}

const COLUMN_TYPES: CSVColumnSpec["type"][] = ["date", "number", "percent", "currency", "string"];

export function CSVPanel({ onError }: { onError: (msg: string | undefined) => void }) {
  const {
    csvUpload,
    csvRows,
    lastCsvReport,
    setCsvUpload,
    setCsvRows,
    setLastCsvReport,
  } = useApp();

  const [busy, setBusy] = useState<string | undefined>();
  const [dateColumn, setDateColumn] = useState<string | undefined>();
  const [metricSelection, setMetricSelection] = useState<Record<string, boolean>>({});
  const fileInputRef = useRef<HTMLInputElement>(null);
  const chartContainerRef = useRef<HTMLDivElement>(null);

  // Restore from IndexedDB on mount
  useEffect(() => {
    (async () => {
      const stored = await idbGet<StoredCsv>(STORAGE_KEY);
      if (stored) {
        setCsvUpload(stored.meta);
        setCsvRows(stored.rows);
      }
    })().catch((e) => onError(String(e)));
  }, [setCsvUpload, setCsvRows, onError]);

  // Derive sensible date + metric defaults whenever upload changes
  useEffect(() => {
    if (!csvUpload) {
      setDateColumn(undefined);
      setMetricSelection({});
      return;
    }
    const dateCol = csvUpload.columns.find((c) => c.type === "date")?.header;
    setDateColumn(dateCol);
    const sel: Record<string, boolean> = {};
    for (const c of csvUpload.columns) {
      if (c.header !== dateCol && c.type !== "string") sel[c.header] = true;
    }
    setMetricSelection(sel);
  }, [csvUpload?.uploadId]);

  async function onFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    onError(undefined);
    setBusy(`Parsing ${file.name}…`);
    try {
      const text = await file.text();
      const parsed = parseCsv(text, file.name);
      await idbPut<StoredCsv>(STORAGE_KEY, { meta: parsed.meta, rows: parsed.rows });
      setCsvUpload(parsed.meta);
      setCsvRows(parsed.rows);
      setLastCsvReport(undefined);
    } catch (err) {
      if (err instanceof CSVError) {
        onError(`${err.message}${err.hint ? " — " + err.hint : ""}`);
      } else {
        onError(`Could not parse CSV: ${String(err)}`);
      }
    } finally {
      setBusy(undefined);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  }

  async function onClear() {
    await idbDelete(STORAGE_KEY);
    setCsvUpload(undefined);
    setCsvRows([]);
    setLastCsvReport(undefined);
  }

  function onSetType(header: string, type: CSVColumnSpec["type"]) {
    if (!csvUpload) return;
    const cols = csvUpload.columns.map((c) =>
      c.header === header ? { ...c, type } : c,
    );
    const newMeta = { ...csvUpload, columns: cols };
    setCsvUpload(newMeta);
    void idbPut<StoredCsv>(STORAGE_KEY, { meta: newMeta, rows: csvRows });
  }

  async function onGenerate() {
    if (!csvUpload || !dateColumn) return;
    onError(undefined);
    setBusy("Building CSV report…");
    try {
      const metricColumns = Object.entries(metricSelection)
        .filter(([, on]) => on)
        .map(([h]) => h);
      const report = buildReport({
        upload: csvUpload,
        rows: csvRows,
        dateColumn,
        metricColumns,
      });
      setLastCsvReport(report);
    } catch (err) {
      onError(err instanceof CSVError ? err.message : `CSV build failed: ${String(err)}`);
    } finally {
      setBusy(undefined);
    }
  }

  async function onDownloadPdf() {
    if (!lastCsvReport) return;
    onError(undefined);
    setBusy("Generating PDF…");
    try {
      const svgEl = chartContainerRef.current?.querySelector<SVGSVGElement>(
        "svg.recharts-surface",
      );
      let chartPng: Uint8Array | undefined;
      if (svgEl) {
        try {
          chartPng = await svgElementToPng(svgEl, 2);
        } catch (e) {
          console.warn("Chart rasterize failed; falling back to pdf-lib chart", e);
        }
      }
      const bytes = await renderCSVReport({
        data: lastCsvReport,
        agency: DEFAULT_AGENCY_BRAND,
        chartPng,
      });
      const blob = new Blob([bytes.buffer as ArrayBuffer], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `agencylens-csv-report-${lastCsvReport.upload.fileName.replace(/\.csv$/i, "")}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) {
      onError(`PDF generation failed: ${String(e)}`);
    } finally {
      setBusy(undefined);
    }
  }

  return (
    <>
      <section className="mb-6 rounded-lg border border-slate-200 bg-white p-6">
        <h2 className="mb-3 text-lg font-medium">CSV upload</h2>
        {csvUpload ? (
          <div className="flex items-center gap-3">
            <span className="inline-flex items-center gap-2 rounded-full bg-emerald-50 px-3 py-1 text-xs font-medium text-emerald-700">
              <span className="h-2 w-2 rounded-full bg-emerald-500" />
              {csvUpload.fileName}
            </span>
            <span className="text-sm text-slate-500">
              {csvUpload.rowCount} {csvUpload.rowCount === 1 ? "row" : "rows"} ·{" "}
              {csvUpload.columns.length} columns · uploaded{" "}
              {new Date(csvUpload.uploadedAt).toLocaleString()}
            </span>
            <Button variant="ghost" size="sm" onClick={onClear} disabled={busy !== undefined}>
              Remove
            </Button>
          </div>
        ) : (
          <div>
            <Button onClick={() => fileInputRef.current?.click()} disabled={busy !== undefined}>
              Upload CSV
            </Button>
            <p className="mt-2 text-xs text-slate-500">
              First row is the header. Date column + at least one numeric column recommended.
            </p>
          </div>
        )}
        <input
          ref={fileInputRef}
          type="file"
          accept=".csv,text/csv"
          className="hidden"
          onChange={onFile}
        />
      </section>

      {csvUpload && (
        <section className="mb-6 rounded-lg border border-slate-200 bg-white p-6">
          <h2 className="mb-3 text-lg font-medium">Configure columns</h2>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="border-b border-slate-200 text-left text-xs uppercase text-slate-500">
                <tr>
                  <th className="py-2 pr-4 font-medium">Column</th>
                  <th className="py-2 pr-4 font-medium">Type</th>
                  <th className="py-2 pr-4 font-medium">Date axis</th>
                  <th className="py-2 pr-4 font-medium">Include as metric</th>
                </tr>
              </thead>
              <tbody>
                {csvUpload.columns.map((c) => (
                  <tr key={c.header} className="border-b border-slate-100">
                    <td className="py-2 pr-4">
                      <span className="font-medium text-slate-800">{c.label}</span>{" "}
                      <span className="text-xs text-slate-500">({c.header})</span>
                    </td>
                    <td className="py-2 pr-4">
                      <select
                        value={c.type}
                        onChange={(e) => onSetType(c.header, e.target.value as CSVColumnSpec["type"])}
                        className="rounded-md border border-slate-200 px-2 py-1 text-xs"
                        disabled={busy !== undefined}
                      >
                        {COLUMN_TYPES.map((t) => (
                          <option key={t} value={t}>
                            {t}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="py-2 pr-4">
                      <input
                        type="radio"
                        name="csv-date-col"
                        checked={dateColumn === c.header}
                        onChange={() => setDateColumn(c.header)}
                        disabled={busy !== undefined}
                      />
                    </td>
                    <td className="py-2 pr-4">
                      <input
                        type="checkbox"
                        checked={metricSelection[c.header] ?? false}
                        onChange={(e) =>
                          setMetricSelection((prev) => ({
                            ...prev,
                            [c.header]: e.target.checked,
                          }))
                        }
                        disabled={busy !== undefined || c.header === dateColumn}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-4 flex items-center gap-3">
            <Button
              onClick={onGenerate}
              disabled={
                busy !== undefined ||
                !dateColumn ||
                Object.values(metricSelection).every((v) => !v)
              }
            >
              Build CSV report
            </Button>
            {busy && <span className="text-sm text-slate-500">{busy}</span>}
          </div>
        </section>
      )}

      {lastCsvReport && (
        <>
          <section className="mb-6 rounded-lg border border-slate-200 bg-white p-6">
            <div className="mb-4 flex items-end justify-between">
              <div>
                <h2 className="text-lg font-medium">{lastCsvReport.upload.fileName}</h2>
                <p className="text-sm text-slate-500">
                  {lastCsvReport.rows.length} rows · date column:{" "}
                  <code>{lastCsvReport.dateColumn}</code>
                </p>
              </div>
              <Button onClick={onDownloadPdf} disabled={busy !== undefined}>
                Download CSV PDF
              </Button>
            </div>
          </section>

          {lastCsvReport.metricColumns[0] && (
            <section className="mb-6 rounded-lg border border-slate-200 bg-white p-6">
              <h3 className="mb-3 text-sm font-medium text-slate-700">
                {lastCsvReport.metricColumns[0]} over time
              </h3>
              <div ref={chartContainerRef} style={{ width: "100%", height: 280 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={lastCsvReport.rows}>
                    <CartesianGrid stroke="#e5e7eb" strokeDasharray="2 4" />
                    <XAxis dataKey={lastCsvReport.dateColumn} tick={{ fontSize: 10 }} />
                    <YAxis tick={{ fontSize: 10 }} />
                    <Tooltip />
                    <Line
                      type="monotone"
                      dataKey={lastCsvReport.metricColumns[0]}
                      stroke="#1d4ed8"
                      strokeWidth={2}
                      dot={false}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </section>
          )}

          <section className="mb-6 rounded-lg border border-slate-200 bg-white p-6">
            <h3 className="mb-3 text-sm font-medium text-slate-700">Data preview</h3>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="border-b border-slate-200 text-left text-xs uppercase text-slate-500">
                  <tr>
                    <th className="py-2 pr-4 font-medium">{lastCsvReport.dateColumn}</th>
                    {lastCsvReport.metricColumns.map((m) => (
                      <th key={m} className="py-2 pr-4 font-medium">
                        {m}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {lastCsvReport.rows.slice(0, 50).map((row, i) => (
                    <tr key={i} className="border-b border-slate-100">
                      <td className="py-2 pr-4">{String(row[lastCsvReport.dateColumn] ?? "")}</td>
                      {lastCsvReport.metricColumns.map((m) => (
                        <td key={m} className="py-2 pr-4">
                          {typeof row[m] === "number"
                            ? row[m].toLocaleString("en-US", { maximumFractionDigits: 4 })
                            : String(row[m] ?? "")}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
              {lastCsvReport.rows.length > 50 && (
                <p className="mt-2 text-xs text-slate-500">
                  Showing first 50 of {lastCsvReport.rows.length} rows. Full data is in the PDF.
                </p>
              )}
            </div>
          </section>
        </>
      )}
    </>
  );
}
